import { ChangeEvent, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Row,
  Spinner
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import {
  AppFeedbackStatusFilter,
  AppFeedbackStatus,
  getSubmittedFeedback,
  AppFeedbackResponse,
  PaginatedFeedbackResponse,
  updateFeedbackStatus
} from 'services/api/feedback';

const FEEDBACK_PAGE_SIZE_OPTIONS = [10, 20, 50];
const FEEDBACK_STATUS_FILTER_OPTIONS: AppFeedbackStatusFilter[] = [
  'active',
  'resolved',
  'all'
];

const feedbackBreadcrumbItems = [
  {
    label: 'Dashboard',
    url: '/home'
  },
  {
    label: 'App Feedback',
    active: true
  }
];

const formatFeedbackTimestamp = (value?: string) => {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));
};

const resolveFeedbackMeta = (feedback: AppFeedbackResponse) => {
  const name = feedback.submittedByName?.trim();
  const email = feedback.submittedByEmail?.trim();

  if (name && email) {
    return `${name} (${email})`;
  }

  return name || email || 'Unknown user';
};

const resolveFeedbackStatus = (
  feedback: AppFeedbackResponse
): AppFeedbackStatus => {
  return feedback.status === 'resolved' ? 'resolved' : 'active';
};

const getPaginationItems = (currentPage: number, totalPages: number) => {
  if (totalPages <= 1) {
    return [];
  }

  const pages = new Set<number>([1, totalPages, currentPage]);

  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) {
      pages.add(page);
    }
  }

  const sortedPages = [...pages].sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];

  sortedPages.forEach((page, index) => {
    const previousPage = sortedPages[index - 1];

    if (previousPage && page - previousPage > 1) {
      items.push('ellipsis');
    }

    items.push(page);
  });

  return items;
};

const resolveErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.data?.message ||
  error?.message ||
  'Failed to load feedback.';

interface AdminFeedbackQueryData {
  feedbackItems: AppFeedbackResponse[];
  total: number;
}

const AdminFeedback = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [draftSearch, setDraftSearch] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] =
    useState<AppFeedbackStatusFilter>('active');
  const [statusActionError, setStatusActionError] = useState<string>('');

  const { data, isLoading, isError, error, isFetching } = useQuery<
    PaginatedFeedbackResponse,
    unknown,
    AdminFeedbackQueryData
  >(
    ['admin-feedback', page, limit, search, statusFilter],
    () =>
      getSubmittedFeedback({
        page,
        limit,
        search: search || undefined,
        status: statusFilter
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      select: response => ({
        feedbackItems: response.data,
        total: response.total
      })
    }
  );

  const feedbackItems = data?.feedbackItems || [];
  const total = data?.total || 0;
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const paginationItems = useMemo(
    () => getPaginationItems(page, totalPages),
    [page, totalPages]
  );

  const updateStatusMutation = useMutation(
    ({
      feedbackId,
      status
    }: {
      feedbackId: string;
      status: AppFeedbackStatus;
    }) => updateFeedbackStatus(feedbackId, { status }),
    {
      onSuccess: updatedFeedback => {
        setStatusActionError('');
        toast.success(
          resolveFeedbackStatus(updatedFeedback) === 'resolved'
            ? 'Feedback marked as resolved'
            : 'Feedback marked as active'
        );
        queryClient.invalidateQueries(['admin-feedback']);
      },
      onError: (mutationError: any) => {
        setStatusActionError(resolveErrorMessage(mutationError));
      }
    }
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraftSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    setPage(1);
    setSearch(draftSearch.trim());
  };

  const handleSearchReset = () => {
    setDraftSearch('');
    setPage(1);
    setSearch('');
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleStatusFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value as AppFeedbackStatusFilter);
    setPage(1);
  };

  return (
    <div>
      <PageBreadcrumb items={feedbackBreadcrumbItems} />
      <Row className="g-3 mb-4 align-items-end">
        <Col lg={7}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h2 className="mb-0">App Feedback</h2>
            {isFetching && !isLoading && (
              <Spinner animation="border" size="sm" />
            )}
          </div>
          <p className="text-700 mb-0 mt-2">
            Review submitted ideas, notes, and screenshots from users.
          </p>
        </Col>
        <Col lg={5}>
          <div className="d-flex gap-2 justify-content-lg-end">
            <SearchBox
              placeholder="Search feedback, email, route..."
              onChange={handleSearchChange}
              value={draftSearch}
              className="w-100"
            />
            <Button variant="primary" onClick={handleSearchSubmit}>
              Search
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleSearchReset}
              disabled={!draftSearch && !search}
            >
              Clear
            </Button>
          </div>
        </Col>
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="text-700 fs-9">
              Showing {feedbackItems.length} of {total} submissions
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-700 fs-9">Status</span>
              <Form.Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{ width: 120 }}
              >
                {FEEDBACK_STATUS_FILTER_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </Form.Select>
              <span className="text-700 fs-9">Per page</span>
              <Form.Select
                value={limit}
                onChange={handlePageSizeChange}
                style={{ width: 88 }}
              >
                {FEEDBACK_PAGE_SIZE_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Col>
      </Row>

      {isError && (
        <Alert variant="danger" className="mb-4">
          {resolveErrorMessage(error)}
        </Alert>
      )}

      {!!statusActionError && (
        <Alert variant="danger" className="mb-4">
          {statusActionError}
        </Alert>
      )}

      {!isError && isLoading && (
        <Card className="border border-200 shadow-none">
          <Card.Body className="py-6 text-center">
            <Spinner animation="border" />
          </Card.Body>
        </Card>
      )}

      {!isError && !isLoading && feedbackItems.length === 0 && (
        <Card className="border border-200 shadow-none">
          <Card.Body className="py-6 text-center text-700">
            {search
              ? 'No feedback matched the current search.'
              : 'No feedback has been submitted yet.'}
          </Card.Body>
        </Card>
      )}

      {!isError &&
        !isLoading &&
        feedbackItems.map(feedback => (
          <Card
            key={feedback.id}
            className="border border-200 shadow-none mb-3"
          >
            <Card.Body>
              <Row className="g-3">
                <Col lg={8}>
                  <div className="d-flex justify-content-between gap-3 flex-wrap mb-2">
                    <div>
                      <h5 className="mb-1">{resolveFeedbackMeta(feedback)}</h5>
                      <div className="text-700 fs-9">
                        Submitted {formatFeedbackTimestamp(feedback.createdAt)}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge
                        bg={
                          resolveFeedbackStatus(feedback) === 'resolved'
                            ? 'success'
                            : 'warning'
                        }
                        className="text-uppercase"
                      >
                        {resolveFeedbackStatus(feedback)}
                      </Badge>
                      {feedback.source && (
                        <div className="text-700 fs-9 text-uppercase">
                          {feedback.source}
                        </div>
                      )}
                    </div>
                  </div>
                  <p
                    className="mb-3 text-900"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {feedback.message}
                  </p>
                  <div className="d-flex flex-wrap gap-3 fs-9 text-700">
                    {feedback.route && <span>Route: {feedback.route}</span>}
                    {feedback.pageUrl && (
                      <span className="text-break">
                        Page: {feedback.pageUrl}
                      </span>
                    )}
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="bg-light rounded-2 p-3 h-100">
                    <div className="mb-3">
                      <Button
                        variant={
                          resolveFeedbackStatus(feedback) === 'resolved'
                            ? 'outline-warning'
                            : 'outline-success'
                        }
                        size="sm"
                        disabled={
                          updateStatusMutation.isLoading &&
                          updateStatusMutation.variables?.feedbackId ===
                            feedback.id
                        }
                        onClick={() =>
                          updateStatusMutation.mutate({
                            feedbackId: feedback.id,
                            status:
                              resolveFeedbackStatus(feedback) === 'resolved'
                                ? 'active'
                                : 'resolved'
                          })
                        }
                      >
                        {updateStatusMutation.isLoading &&
                        updateStatusMutation.variables?.feedbackId ===
                          feedback.id
                          ? 'Updating...'
                          : resolveFeedbackStatus(feedback) === 'resolved'
                          ? 'Mark as active'
                          : 'Mark as resolved'}
                      </Button>
                    </div>
                    <div className="fs-9 text-700 mb-2">Attachment</div>
                    {feedback.sharePointFileUrl ? (
                      <Button
                        as="a"
                        href={feedback.sharePointFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        variant="outline-primary"
                        size="sm"
                      >
                        View uploaded image
                      </Button>
                    ) : (
                      <div className="text-700 fs-9 mb-3">
                        No image uploaded
                      </div>
                    )}
                    {feedback.pageUrl && (
                      <div className="mt-3">
                        <Button
                          as="a"
                          href={feedback.pageUrl}
                          target="_blank"
                          rel="noreferrer"
                          variant="outline-secondary"
                          size="sm"
                        >
                          Open source page
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}

      {!isError && !isLoading && totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.Prev
              disabled={page === 1}
              onClick={() =>
                setPage(currentPage => Math.max(currentPage - 1, 1))
              }
            />
            {paginationItems.map((item, index) =>
              item === 'ellipsis' ? (
                <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
              ) : (
                <Pagination.Item
                  key={item}
                  active={item === page}
                  onClick={() => setPage(item)}
                >
                  {item}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() =>
                setPage(currentPage => Math.min(currentPage + 1, totalPages))
              }
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;
